import axiosInstance from '../../api/axios';

const baseUrl = '/account';

const AccountService = {
  /**
   * Upload a new avatar for the user. The backend requires the avatar file.
   * To delete the avatar, use the user update endpoint with delete_avatar: true instead.
   */
  updateAvatar: async (id, avatarFile) => {
    try {
      if (!avatarFile) {
        throw new Error('Avatar file is required for upload');
      }
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const response = await axiosInstance.post(`${baseUrl}/avatar/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        return response.data.user;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getAccountDevices: async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/devices-sessions`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  updatePersonalInfo: async (userInfo) => {
    try {
      const response = await axiosInstance.put(`${baseUrl}/update`, userInfo);
      if (response.data.success) {
        return response.data.user;
      } else {
        throw new Error(response.data.error || response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      } else if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      } else {
        throw error;
      }
    }
  },

  checkUserPassword: async (payload) => {
    try {
      const response = await axiosInstance.post(`${baseUrl}/check-password`, payload);
      if (response.data.success) {
        return response.data.password_matches;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      } else {
        throw error;
      }
    }
  },

  deleteUserDevice: async (deviceIdentifier) => {
    try {
      const response = await axiosInstance.delete(`${baseUrl}/delete-device`, {
        data: { device_identifier: deviceIdentifier },
      });
      if (response.data.success) {
        return response.data.message;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  changeUserPassword: async (passwordData) => {
    try {
      const response = await axiosInstance.put(`${baseUrl}/update-password`, passwordData);
      if (response.data.success) {
        return response.data.message;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  getUserProgress: async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/progress`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },
  getUserActiveChapters: async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/active-chapters`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      } else {
        throw error;
      }
    }
  },
  getUserFavourites: async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/favourites`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },

  addToFavourites: async (itemId, itemType) => {
    try {
      const response = await axiosInstance.post(`${baseUrl}/favourites`, { item_id: itemId, item_type: itemType });
      if (response.status === 201) {
        return response.data.favourite;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        throw new Error(Object.values(error.response.data.errors).join(', '));
      } else {
        throw error;
      }
    }
  },

  removeFromFavourites: async (itemId, itemType) => {
    try {
      const response = await axiosInstance.delete(`${baseUrl}/favourites/remove`, {
        data: { item_id: itemId, item_type: itemType },
      });
      if (response.status === 200) {
        return response.data.message;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        throw new Error(Object.values(error.response.data.errors).join(', '));
      } else {
        throw error;
      }
    }
  },
  updateUserEducationalLevel: async (educationalLevelId) => {
    try {
      const response = await axiosInstance.put(`${baseUrl}/update-educational-level`, { educational_level_id: educationalLevelId });
      if (response.data.success) {
        return response.data.user;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },
  updateUserEmail: async (email) => {
    try {
      const response = await axiosInstance.put(`${baseUrl}/update-email`, { email });
      if (response.data.success) {
        return response.data.user;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      throw error;
    }
  },
  getWeeklyActivity: async (startDate) => {
    try {
      const params = startDate ? { start_date: startDate } : {};
      const response = await axiosInstance.get(`${baseUrl}/activity`, { params });
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      } else {
        throw error;
      }
    }
  },

  getLearningStreak: async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/streak`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      } else {
        throw error;
      }
    }
  },
};

export default AccountService;